import { DropboxFileItem } from '@common/types/dropbox';
import { VictronImage } from '@components/display/images';
import { VictronVideo } from '@components/display/videos';
import { useMediaAssetsContext } from '@mediaAssets/contexts';
import {
    useLazyGetMediaAssetsDropboxDownloadJsonQuery,
    useMediaAssetsFavoriteMutation,
    useMediaAssetsUnfavoriteMutation,
} from '@mediaAssets/services';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, Dialog, DialogContent, DialogProps, IconButton, Stack } from '@mui/material';
import { handleError } from '@utils/common';
import { triggerBrowserDownload } from '@utils/file';
import { clsx } from 'clsx';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

export const MediaAssetsGallery = ({ ...props }: DialogProps) => {
    const { selectedMediaAsset, visibleMediaAssets, setSelectedMediaAsset, setVisibleMediaAssets } =
        useMediaAssetsContext();

    const [showFade, setShowFade] = useState(false);
    const [emblaMainRef, emblaMainApi] = useEmblaCarousel();
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true,
    });
    const [getNewStreamUrl] = useLazyGetMediaAssetsDropboxDownloadJsonQuery();
    const [favoriteFile] = useMediaAssetsFavoriteMutation();
    const [unfavoriteFile] = useMediaAssetsUnfavoriteMutation();
    const intl = useIntl();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
    const [newStreamUrls, setNewStreamUrls] = useState<Record<string, { url: string; url_expires_at: string }>>({});
    const videoRefs = useRef<Record<string, { reset: () => void } | null>>({});

    const { pathname } = useLocation();
    const queryPath = pathname.split('/').filter(Boolean).slice(2).map(decodeURIComponent).join('/');

    const selectedIndex = selectedMediaAsset
        ? visibleMediaAssets.findIndex((asset) => asset.path === selectedMediaAsset.path)
        : 0;

    const onThumbClick = useCallback(
        (index: number) => {
            if (!emblaMainApi || !emblaThumbsApi) return;
            emblaMainApi.scrollTo(index);
        },
        [emblaMainApi, emblaThumbsApi]
    );

    const onSelect = useCallback(
        (index?: number) => {
            if (!emblaMainApi || !emblaThumbsApi) return;

            const newSelectedMediaAsset =
                index !== undefined ? visibleMediaAssets[index] : visibleMediaAssets[emblaMainApi.selectedScrollSnap()];
            setSelectedMediaAsset(newSelectedMediaAsset);
            emblaThumbsApi.scrollTo(index !== undefined ? index : emblaMainApi.selectedScrollSnap());
        },
        [emblaMainApi, emblaThumbsApi, setSelectedMediaAsset, visibleMediaAssets]
    );

    const onScrollChange = useCallback((emblaApi: EmblaCarouselType) => {
        const slidesInView = emblaApi.slidesInView();
        const lastIndex = emblaApi.slideNodes().length - 1;
        setShowFade(!slidesInView.includes(lastIndex));
    }, []);

    const handleNext = () => {
        if (!emblaMainApi) return;
        emblaMainApi.scrollTo(emblaMainApi.selectedScrollSnap() + 1);
    };

    const handlePrev = () => {
        if (!emblaMainApi) return;
        emblaMainApi.scrollTo(emblaMainApi.selectedScrollSnap() - 1);
    };

    const handleDownload = async () => {
        const itemPath = selectedMediaAsset?.path ?? '';
        if (!itemPath || isDownloading) {
            return;
        }

        setIsDownloading(true);
        try {
            await triggerBrowserDownload(
                `/api-professional/dropbox/download/?path=${encodeURIComponent(itemPath)}&type=media_assets`
            );
        } finally {
            setIsDownloading(false);
        }
    };

    const handleToggleFavorite = useCallback(async () => {
        const current = selectedMediaAsset;
        const itemPath = current?.path ?? '';
        if (!current || !itemPath || isTogglingFavorite) {
            return;
        }

        const previousVisibleMediaAssets = visibleMediaAssets;
        const previousSelectedMediaAsset = selectedMediaAsset;
        const toggledFavorite = !current.is_favorite;
        setIsTogglingFavorite(true);

        setVisibleMediaAssets((prev) => {
            const index = prev.findIndex((asset) => asset.path === itemPath);
            if (index === -1) {
                return prev;
            }

            const updatedAsset = { ...prev[index], is_favorite: toggledFavorite };
            const next = [...prev];
            next[index] = updatedAsset;

            return next;
        });
        setSelectedMediaAsset((prev) =>
            prev && prev.path === itemPath ? { ...prev, is_favorite: toggledFavorite } : prev
        );

        try {
            if (current.is_favorite) {
                await unfavoriteFile({ path: itemPath, folderPath: queryPath }).unwrap();
            } else {
                await favoriteFile({ path: itemPath, folderPath: queryPath }).unwrap();
            }
        } catch (error) {
            setVisibleMediaAssets(previousVisibleMediaAssets);
            setSelectedMediaAsset(previousSelectedMediaAsset);
            handleError('Error updating favorite status', error);
        } finally {
            setIsTogglingFavorite(false);
        }
    }, [
        emblaMainApi,
        emblaThumbsApi,
        favoriteFile,
        isTogglingFavorite,
        selectedMediaAsset,
        setSelectedMediaAsset,
        setVisibleMediaAssets,
        unfavoriteFile,
        visibleMediaAssets,
    ]);

    const handleVideoError = async (asset: DropboxFileItem) => {
        // If the video fails to load, try to get a new stream URL, due to them having a max lifetime of 4 hours.
        if (
            newStreamUrls[asset.path ?? '']?.url_expires_at &&
            new Date(newStreamUrls[asset.path ?? '']?.url_expires_at) > new Date()
        ) {
            // If the current stream URL is still valid, do not attempt to get a new one to avoid an infinite loop.
            return;
        }
        const response = await getNewStreamUrl(asset.path ?? '').unwrap();
        if (response?.stream_url && asset.path) {
            setNewStreamUrls((prev) => ({
                ...prev,
                [asset.path ?? '']: {
                    url: response.stream_url ?? '',
                    url_expires_at: response.url_expires_at ?? '',
                },
            }));
            // Reset the video element to reload the new URL
            videoRefs.current[asset.path ?? '']?.reset();
        }
    };

    useEffect(() => {
        if (!emblaMainApi) return;
        const index = selectedMediaAsset
            ? visibleMediaAssets.findIndex((asset) => asset.path === selectedMediaAsset.path)
            : 0;
        onSelect(index);
        if (index !== -1) {
            emblaMainApi.scrollTo(index);
        }

        emblaMainApi.on('select', () => onSelect()).on('reInit', () => onSelect());

        return () => {
            emblaMainApi.off('select', () => onSelect()).off('reInit', () => onSelect());
            setSelectedMediaAsset(null); // Reset selected media asset when the gallery is closed
        };
    }, [emblaMainApi, onSelect]);

    // Fade effect for the thumbnail carousel when the last thumbnail is not fully visible
    useEffect(() => {
        if (!emblaThumbsApi) return;
        emblaThumbsApi.on('scroll', onScrollChange).on('slidesInView', onScrollChange);
        onScrollChange(emblaThumbsApi);

        return () => {
            emblaThumbsApi?.off('scroll', onScrollChange).off('slidesInView', onScrollChange);
        };
    }, [emblaThumbsApi, onScrollChange]);

    return (
        <Dialog
            {...props}
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: '28px',
                        maxWidth: { lg: '800px' },
                        minWidth: { lg: '1200px' },
                        minHeight: { lg: '600px' },
                    },
                },
            }}
        >
            <Stack direction="row" gap={2} position="absolute" top={24} left={24} zIndex={1}>
                <IconButton className="black-border" color="black" onClick={handleDownload}>
                    <DownloadIcon />
                </IconButton>
                <IconButton
                    className="black-border"
                    color="black"
                    onClick={handleToggleFavorite}
                    loading={isTogglingFavorite}
                >
                    {selectedMediaAsset?.is_favorite ? <StarIcon color="primary-300" /> : <StarBorderIcon />}
                </IconButton>
            </Stack>
            <Box
                position="absolute"
                top={24}
                right={24}
                zIndex={1}
                onClick={() => props.onClose?.({}, 'backdropClick')}
            >
                <IconButton color="white" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent className="py-[32px] flex flex-col justify-center">
                <Box
                    maxWidth="800px"
                    mx="auto"
                    pt={{ xs: '50px', lg: 0 }}
                    flexGrow={1}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <Box className="overflow-hidden" ref={emblaMainRef}>
                        <Stack direction="row" className="touch-pan-y touch-pinch-zoom" ml="-10px" alignItems="center">
                            {visibleMediaAssets.map((asset, index) => (
                                <Box key={`media-asset-slide-${index}`} className="min-w-0 flex-[0_0_100%] pl-[10px]">
                                    {asset.file_type === 'image' ? (
                                        <VictronImage
                                            src={asset.thumbnail}
                                            alt={intl.formatMessage(
                                                {
                                                    id: 'mediaAssets.imageThumbnail',
                                                    defaultMessage: 'Media Item Thumbnail of {name}',
                                                },
                                                { name: asset.name }
                                            )}
                                            className="mx-auto"
                                        />
                                    ) : (
                                        <VictronVideo
                                            controls
                                            src={newStreamUrls[asset.path ?? '']?.url ?? asset.stream_url}
                                            ref={(ref) => {
                                                if (ref && asset.path) {
                                                    videoRefs.current[asset.path] = ref;
                                                }
                                            }}
                                            onError={() => handleVideoError(asset)}
                                            className="mx-auto"
                                            skeletonProps={{
                                                height: 450,
                                            }}
                                            errorProps={{
                                                height: 450,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </Box>
                <Box maxWidth={{ xs: '100%', sm: '800px' }} mx="auto">
                    <Stack
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                        mt="57px"
                        mx={{ xs: 0, md: '72px' }}
                        gap="16px"
                    >
                        <IconButton onClick={handlePrev} disabled={selectedIndex <= 0} className="h-min" color="black">
                            <ArrowBackIosNewIcon />
                        </IconButton>
                        <Box className="relative overflow-hidden" ref={emblaThumbsRef}>
                            <Stack direction="row" ml="-16px" justifyContent="center">
                                {visibleMediaAssets.map((asset, index) => (
                                    <button
                                        key={`media-asset-thumb-${index}`}
                                        className="min-w-0 flex-[0_0_76px] pl-[16px]"
                                        onClick={() => onThumbClick(index)}
                                    >
                                        <Box
                                            borderRadius="4px"
                                            p="2px"
                                            className={clsx({
                                                'black-border border-2': selectedIndex !== index,
                                                'border-2 border-primary-600': selectedIndex === index,
                                            })}
                                        >
                                            <VictronImage
                                                src={asset.thumbnail}
                                                alt={intl.formatMessage(
                                                    {
                                                        id: 'mediaAssets.imageThumbnail',
                                                        defaultMessage: 'Media Item Thumbnail of {name}',
                                                    },
                                                    { name: asset.name }
                                                )}
                                                width={52}
                                                height={52}
                                            />
                                        </Box>
                                    </button>
                                ))}
                            </Stack>
                            <div
                                className={clsx(
                                    'absolute top-0 right-0 h-full w-[5%] bg-gradient-to-r from-transparent to-white',
                                    {
                                        hidden: !showFade,
                                    }
                                )}
                            />
                        </Box>
                        <IconButton
                            onClick={handleNext}
                            disabled={selectedIndex >= visibleMediaAssets.length - 1}
                            className="h-min"
                            color="black"
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
